import React from "react";
import { Sun, Droplet, Sprout, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";

const DEFAULT_COVER = "https://images.unsplash.com/photo-1579167728798-a1cf3d595960?auto=format&fit=crop&w=600&q=80";

export default function PlantCard({ plant, onRemove, onClick, compact = false }) {
  // Cover preference: user photo > AI catalog image (if catalog_id) > generic
  const cover = plant.photo_path
    ? `${API}/files/${plant.photo_path}`
    : (plant.id && plant.category !== "Custom" && plant.catalog_id)
      ? `${API}/catalog/${plant.catalog_id}/image`
      : plant.catalog_id // catalog browse view: plant.id is the catalog id itself
        ? `${API}/catalog/${plant.catalog_id}/image`
        : (plant.id ? `${API}/catalog/${plant.id}/image` : DEFAULT_COVER);
  return (
    <div
      onClick={onClick}
      className="sage-card overflow-hidden cursor-pointer flex flex-col"
      data-testid={`plant-card-${plant.id || plant.name}`}
    >
      <div
        className="h-32 relative bg-cover bg-center bg-muted"
        style={{ backgroundImage: `url(${cover}), url(${DEFAULT_COVER})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-card/95 via-card/30 to-transparent" />
        <span className="absolute top-2 left-2 label-overline bg-card/80 backdrop-blur px-2 py-1 rounded">
          {plant.category}
        </span>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-serif text-xl font-light leading-tight" data-testid={`plant-name-${plant.id || plant.name}`}>
          {plant.name}
        </h3>
        <div className="mt-2 space-y-1 text-xs text-muted-foreground">
          <Row icon={Sun}><span className="font-medium text-foreground">Sun:</span> {plant.sun}</Row>
          <Row icon={Droplet}><span className="font-medium text-foreground">Water:</span> {plant.water}</Row>
          <Row icon={Sprout}><span className="font-medium text-foreground">Season:</span> {plant.season}</Row>
        </div>
        {!compact && (
          <p className="text-xs text-muted-foreground mt-3 line-clamp-2 leading-relaxed">{plant.description}</p>
        )}
        {onRemove && (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onRemove(plant); }}
            className="mt-3 self-start text-destructive/80 hover:text-destructive hover:bg-destructive/5 gap-1"
            data-testid={`remove-plant-${plant.id}`}
          >
            <Trash2 className="w-3.5 h-3.5" /> Remove
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-1.5">
      <Icon className="w-3.5 h-3.5 mt-0.5 text-accent" strokeWidth={1.5} />
      <span>{children}</span>
    </div>
  );
}
